/**
 * TemplateFieldFiller
 *
 * Inline template field editor for the templates-v2 instantiation flow.
 * Renders each field with type-appropriate inputs, source badges,
 * and validation feedback.
 *
 * Modeled after CorrespondenceFieldEditor with v2 field types.
 */

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';
import { type ReactNode, useCallback, useMemo } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useListAttorneysQuery } from '@/services/api/attorneysService';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import {
  type ArrayItemProperty,
  type ContextCategoryMeta,
  type ReadTemplateField,
  useTemplateFieldsQuery,
} from '@/services/api/generated/templatesV2Service';
import {
  type GetMatterContextParams,
  useGetMatterFlattenedContextQuery,
} from '@/services/api/matterFlattenedContextService';
import { useGetMatterQuery } from '@/services/api/mattersService';
import type { CoverageSchema } from '@/services/types/coverage-types';

import type { MatterContext } from '../utils/fieldValidation';
import { normalizeTemplateFields } from '../utils/fieldValidation';
import { FieldSourceBadge } from './FieldSourceBadge';
import {
  CATEGORY_TO_SLOT,
  type EntitySlotName,
  getCoverageDisplayName,
  getPolicyNumber,
} from './TemplateEntitySlots';
import { type ResolvedField, useFieldValues } from './useFieldValues';

// ============================================================================
// Entity Bindings
// ============================================================================

/**
 * Entity binding selections for the matter context API.
 * Keys match GetMatterContextParams (minus matterId).
 */
export type EntityBindings = Omit<GetMatterContextParams, 'matterId'>;

// ============================================================================
// Props
// ============================================================================

interface TemplateFieldFillerProps {
  /** Template ID to fetch fields for */
  templateId: string;
  /** Optional matter ID for context resolution and coverage fetching */
  matterId?: string;
  /** Current field overrides */
  overrides: Record<string, string>;
  /** Called when a single field value changes */
  onFieldChange: (key: string, value: string) => void;
  /** Whether any overrides differ from committed state */
  isDirty: boolean;
  /** Called when user clicks Apply */
  onApply: () => void;
  /** Called when user clicks Discard */
  onDiscard: () => void;
  /** Read-only mode */
  readOnly?: boolean;
  /** Optional className */
  className?: string;
  /** Entity binding selections (coverage IDs, attorney ID, etc.) */
  entityBindings?: EntityBindings;
  /** Called when entity bindings change */
  onEntityBindingChange?: (bindings: EntityBindings) => void;
}

// ============================================================================
// Component
// ============================================================================

/** Reverse lookup: binding key → SlotSelector slot name (frontend concern for SlotSelector). */
const BINDING_TO_SLOT: Partial<Record<keyof EntityBindings, EntitySlotName>> = {
  coverage1pId: '1p',
  coverage3pId: '3p',
  coverageHealthId: 'health',
};

type FieldGroupEntry = {
  key: string;
  label: string;
  variant: 'context' | 'manual';
  fields: ResolvedField[];
  filledCount: number;
  totalCount: number;
  missingRequiredCount: number;
  /** Category metadata from the API (label, binding_key, entity_absent_hint). */
  meta: ContextCategoryMeta | null;
  /** Hint shown when the backing entity is absent from the matter. */
  entityAbsentHint: string | null;
};

export const TemplateFieldFiller = ({
  templateId,
  matterId,
  overrides,
  onFieldChange,
  isDirty,
  onApply,
  onDiscard,
  readOnly = false,
  className,
  entityBindings = {},
  onEntityBindingChange,
}: TemplateFieldFillerProps) => {
  const {
    data: fieldsData,
    isLoading,
    error,
  } = useTemplateFieldsQuery({ id: templateId }, { skip: !templateId });

  // Fetch matter details to get firmId for attorney listing
  const { data: matterDetails } = useGetMatterQuery(matterId ?? '', {
    skip: !matterId,
  });
  const firmId = matterDetails?.firm?.id;
  const defaultRepresentingAttorneyName = useMemo(() => {
    const representingAttorney = matterDetails?.representing_attorney;
    if (!representingAttorney) {
      return null;
    }
    if (typeof representingAttorney === 'string') {
      return representingAttorney;
    }
    return representingAttorney.name ?? null;
  }, [matterDetails?.representing_attorney]);

  // Fetch attorneys for the firm
  const { data: attorneys } = useListAttorneysQuery(
    { firm_id: firmId ?? '' },
    { skip: !firmId },
  );

  // Fetch coverages when a matter is selected, for embedding slot selectors
  const { data: coverages } = useGetCoveragesQuery(
    { matterId: matterId ?? '' },
    { skip: !matterId },
  );

  // Group coverages by slot for inline slot selectors
  const coveragesBySlot = useMemo(() => {
    const result: Record<EntitySlotName, CoverageSchema[]> = {
      '1p': [],
      '3p': [],
      health: [],
    };
    if (!coverages) return result;
    coverages.forEach((coverage) => {
      const slotName = coverage.category ? CATEGORY_TO_SLOT[coverage.category] : null;
      if (slotName) {
        result[slotName].push(coverage);
      }
    });
    return result;
  }, [coverages]);

  // Update a single entity binding key
  const handleBindingChange = useCallback(
    (key: keyof EntityBindings, value: string | null) => {
      if (!onEntityBindingChange) return;
      const next = { ...entityBindings };
      if (value && value.trim() !== '') {
        next[key] = value;
      } else {
        delete next[key];
      }
      onEntityBindingChange(next);
    },
    [entityBindings, onEntityBindingChange],
  );

  // Context params derive directly from entity bindings
  const contextParams = useMemo(
    () => ({
      matterId: matterId ?? '',
      ...entityBindings,
    }),
    [matterId, entityBindings],
  );

  // Fetch resolved matter context — re-fetches when entity bindings change
  const { data: rawContext } = useGetMatterFlattenedContextQuery(contextParams, {
    skip: !matterId,
  });

  // Coerce null values to undefined for MatterContext compatibility
  const matterContext = useMemo<MatterContext>(() => {
    if (!rawContext) return {};
    return rawContext;
  }, [rawContext]);

  const templateFields = useMemo<ReadTemplateField[]>(
    () => normalizeTemplateFields(fieldsData),
    [fieldsData],
  );

  const { fields, isValid, missingFields } = useFieldValues(
    templateFields,
    matterContext,
    overrides,
  );

  // Group fields by context_category, with manual fields in a separate group at the end
  const fieldGroups = useMemo(() => {
    const requiredFirst = (a: ResolvedField, b: ResolvedField) =>
      Number(b.required) - Number(a.required);

    const groupMap = new Map<string, ResolvedField[]>();

    fields.forEach((resolved) => {
      const key = resolved.field.context_category ?? 'manual';
      const group = groupMap.get(key);
      if (group) {
        group.push(resolved);
      } else {
        groupMap.set(key, [resolved]);
      }
    });

    // Sort within each group: required first
    groupMap.forEach((group) => {
      group.sort(requiredFirst);
    });

    // Build result with completion stats
    const hasMatter = !!matterId;

    const buildEntry = (key: string, variant: 'context' | 'manual', groupFields: ResolvedField[]): FieldGroupEntry => {
      const meta = groupFields[0]?.field?.context_category_meta ?? null;
      const label = variant === 'manual' ? 'Manual Entry' : (meta?.label ?? key);
      const filledCount = groupFields.filter((r) => !r.isMissing || !r.required).length;
      const missingRequiredCount = groupFields.filter((r) => r.required && r.isMissing).length;
      const hasContextData = groupFields.some((r) => r.valueInfo.source === 'context');
      const entityAbsentHint =
        !hasMatter || variant === 'manual' || hasContextData
          ? null
          : (meta?.entity_absent_hint ?? null);
      return { key, label, variant, fields: groupFields, filledCount, totalCount: groupFields.length, missingRequiredCount, meta: meta ?? null, entityAbsentHint };
    };

    // Entity groups first (in insertion order), manual group last
    const result: FieldGroupEntry[] = [];
    groupMap.forEach((groupFields, key) => {
      if (key !== 'manual') {
        result.push(buildEntry(key, 'context', groupFields));
      }
    });
    const manualFields = groupMap.get('manual');
    if (manualFields) {
      result.push(buildEntry('manual', 'manual', manualFields));
    }

    return result;
  }, [fields, matterId]);

  // Auto-expand groups that have missing required fields, keep all expanded initially
  const defaultOpenGroupsKey = useMemo(
    () => fieldGroups.map((g) => `${g.key}:${g.missingRequiredCount}`).join(','),
    [fieldGroups],
  );
  const defaultOpenGroups = useMemo(
    () => fieldGroups.filter((g) => g.missingRequiredCount > 0 || g.fields.length > 0).map((g) => g.key),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultOpenGroupsKey],
  );

  // Build a compact entity selector for a group's accordion header
  const renderEntitySelector = (group: FieldGroupEntry) => {
    const bindingKey = group.meta?.binding_key as keyof EntityBindings | undefined;
    if (!bindingKey || !onEntityBindingChange || !matterId) return null;

    // Coverage selector
    const slotName = BINDING_TO_SLOT[bindingKey];
    if (slotName) {
      const slotCoverages = coveragesBySlot[slotName];
      if (slotCoverages.length === 0) return null;
      const selectedCoverage = slotCoverages.find((c) => c.id === entityBindings[bindingKey]);
      return (
        <Select
          value={entityBindings[bindingKey] ?? '__none__'}
          onValueChange={(v: string) => handleBindingChange(bindingKey, v === '__none__' ? null : v)}
          disabled={readOnly}
        >
          <SelectTrigger className="h-7 max-w-[180px] gap-1 text-[11px] font-medium">
            <SelectValue placeholder="Select coverage…">
              {selectedCoverage
                ? getCoverageDisplayName(selectedCoverage)
                : 'Select coverage…'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__" className="text-zinc-500">
              No selection
            </SelectItem>
            {slotCoverages.map((coverage) => {
              const policyNumber = getPolicyNumber(coverage);
              return (
                <SelectItem key={coverage.id} value={coverage.id} className="py-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{getCoverageDisplayName(coverage)}</span>
                    {policyNumber && (
                      <span className="text-[11px] font-medium text-zinc-500">
                        Policy: {policyNumber}
                      </span>
                    )}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      );
    }

    // Attorney selector
    if (bindingKey === 'attorneyId' && attorneys && attorneys.length > 0) {
      const activeAttorneys = attorneys.filter((a) => a.is_active);
      if (activeAttorneys.length === 0) return null;
      const selected = activeAttorneys.find((a) => a.id === entityBindings.attorneyId);
      const selectedLabel = selected
        ? `${selected.first_name} ${selected.last_name}`.trim()
        : null;
      const placeholderLabel = defaultRepresentingAttorneyName
        ? `${defaultRepresentingAttorneyName} (default)`
        : 'No attorney';
      return (
        <Select
          value={entityBindings.attorneyId ?? '__default__'}
          onValueChange={(v: string) => handleBindingChange('attorneyId', v === '__default__' ? null : v)}
          disabled={readOnly}
        >
          <SelectTrigger className="h-7 max-w-[180px] gap-1 text-[11px] font-medium">
            <SelectValue placeholder="Use default">
              {selectedLabel ?? placeholderLabel}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__" className="text-zinc-500">
              {defaultRepresentingAttorneyName
                ? `${defaultRepresentingAttorneyName} (default)`
                : 'No selection'}
            </SelectItem>
            {activeAttorneys.map((attorney) => (
              <SelectItem key={attorney.id} value={attorney.id} className="py-2">
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">
                    {attorney.first_name} {attorney.last_name}
                  </span>
                  {attorney.title && (
                    <span className="text-[11px] font-medium text-zinc-500">
                      {attorney.title}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return null;
  };

  if (isLoading) {
    return <FieldFillerSkeleton className={className} />;
  }

  if (error) {
    return (
      <div className={cn('text-sm font-medium text-red-600 dark:text-red-400', className)}>
        Failed to load template fields.
      </div>
    );
  }

  if (fields.length === 0) {
    return (
      <div className={cn('text-sm font-medium text-zinc-500', className)}>
        No fillable fields in this template.
      </div>
    );
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      <Accordion type="multiple" defaultValue={defaultOpenGroups}>
        {fieldGroups.map((group) => (
          <AccordionItem
            key={group.key}
            value={group.key}
            className={cn(
              'mb-1.5 rounded-md border px-2.5 last:mb-0',
              group.variant === 'context'
                ? 'border-zinc-200/80 bg-zinc-50/70 dark:border-zinc-700/80 dark:bg-zinc-900/60'
                : 'border-zinc-200/80 bg-white dark:border-zinc-700/80 dark:bg-zinc-950/70',
            )}
          >
            <GroupHeader
              group={group}
              entitySelector={renderEntitySelector(group)}
            />
            <AccordionContent>
              <div className="space-y-2 pb-0.5">
                {/* Entity absent hint */}
                {group.entityAbsentHint && (
                  <p className="py-1 pl-0.5 text-[10px] font-medium leading-tight text-zinc-500 dark:text-zinc-400">
                    {group.entityAbsentHint} — values can be entered manually.
                  </p>
                )}
                {group.fields.map((resolved) => (
                  <FieldRow
                    key={resolved.field.id as string}
                    resolved={resolved}
                    onValueChange={onFieldChange}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Save/Discard bar */}
      {isDirty && !readOnly && (
        <div className="sticky bottom-0 z-10 flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white/95 p-1.5 backdrop-blur dark:border-zinc-700 dark:bg-zinc-900/95">
          <button
            type="button"
            onClick={onApply}
            className="rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={onDiscard}
            className="rounded-md px-2.5 py-1 text-[11px] font-semibold text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
          >
            Discard
          </button>
          {!isValid && (
            <span className="ml-auto text-[11px] font-medium text-amber-600 dark:text-amber-400">
              {missingFields.length} required field{missingFields.length !== 1 ? 's' : ''} missing
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Group Header (replaces AccordionTrigger for entity-selector integration)
// ============================================================================

const GroupHeader = ({
  group,
  entitySelector,
}: {
  group: FieldGroupEntry;
  entitySelector: ReactNode;
}) => (
  <AccordionPrimitive.Header className="flex items-center gap-2 py-2">
    <AccordionPrimitive.Trigger className="focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-center gap-1.5 rounded-md text-left outline-none focus-visible:ring-[3px] [&[data-state=open]>svg]:rotate-180">
      <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform duration-200" />
      <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">
        {group.label}
      </span>
      <span
        className={cn(
          'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
          group.missingRequiredCount > 0
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400'
            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
        )}
      >
        {group.filledCount}/{group.totalCount}
      </span>
      {group.missingRequiredCount > 0 && (
        <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">
          {group.missingRequiredCount} required
        </span>
      )}
    </AccordionPrimitive.Trigger>
    {entitySelector && (
      <div className="shrink-0">
        {entitySelector}
      </div>
    )}
  </AccordionPrimitive.Header>
);

// ============================================================================
// Field Row
// ============================================================================

interface FieldRowProps {
  resolved: ResolvedField;
  onValueChange: (overrideKey: string, value: string) => void;
  readOnly: boolean;
}

const FieldRow = ({
  resolved,
  onValueChange,
  readOnly,
}: FieldRowProps) => {
  const { field, overrideKey, displayName, valueInfo, required, isMissing } =
    resolved;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange(overrideKey, e.target.value);
    },
    [onValueChange, overrideKey],
  );

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange(overrideKey, e.target.checked ? 'true' : 'false');
    },
    [onValueChange, overrideKey],
  );

  // Dispatch to ArrayFieldRow for ARRAY fields with sub-field schema
  if (field.field_type === 'ARRAY' && field.array_item_schema?.length) {
    return (
      <ArrayFieldRow
        resolved={resolved}
        onValueChange={onValueChange}
        readOnly={readOnly}
      />
    );
  }

  const fieldId = field.id as string;
  const errorId = `${fieldId}-error`;

  // Render checkbox separately
  if (field.field_type === 'CHECKBOX') {
    return (
      <div className="flex items-center gap-2 rounded-md border border-zinc-200/80 bg-white/90 px-2.5 py-2 dark:border-zinc-700/80 dark:bg-zinc-950/60">
        <input
          id={fieldId}
          type="checkbox"
          checked={valueInfo.value === 'true'}
          onChange={handleCheckboxChange}
          disabled={readOnly}
          className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 dark:border-zinc-600 disabled:opacity-50"
        />
        <label
          htmlFor={fieldId}
          className={cn(
            'flex items-center gap-1 text-[12px] font-medium',
            isMissing
              ? 'text-red-700 dark:text-red-400'
              : 'text-zinc-700 dark:text-zinc-300',
          )}
        >
          {displayName}
          {required && <span className="text-red-500">*</span>}
          <FieldSourceBadge source={valueInfo.source} isMissing={isMissing} inline />
        </label>
      </div>
    );
  }

  // Determine input type
  const inputType = field.field_type === 'DATE' ? 'date' : 'text';

  const inputClasses = cn(
    'h-8 w-full rounded-md border px-2.5 text-[13px] transition-colors',
    'focus:outline-none focus:ring-1',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    isMissing && 'border-red-300 bg-red-50 focus:ring-red-500 dark:border-red-500/50 dark:bg-red-500/10',
    !isMissing &&
      valueInfo.source === 'override' &&
      'border-violet-300 bg-violet-50/30 dark:border-violet-500/40 dark:bg-violet-500/10',
    !isMissing &&
      valueInfo.source !== 'override' &&
      'border-zinc-300 focus:ring-indigo-500 dark:border-zinc-600',
  );

  return (
    <div className="flex flex-col gap-1 rounded-md border border-zinc-200/80 bg-white/90 px-2.5 py-2 dark:border-zinc-700/80 dark:bg-zinc-950/60">
      <label
        htmlFor={fieldId}
        className={cn(
          'flex items-center gap-x-1 text-[12px] font-medium',
          isMissing
            ? 'text-red-700 dark:text-red-400'
            : 'text-zinc-700 dark:text-zinc-300',
        )}
      >
        <span>{displayName}</span>
        {required && <span className="text-red-500">*</span>}
        <FieldSourceBadge source={valueInfo.source} isMissing={isMissing} inline />
      </label>
      <input
        id={fieldId}
        type={inputType}
        value={valueInfo.value}
        onChange={handleChange}
        disabled={readOnly}
        placeholder={getPlaceholder(field)}
        aria-describedby={isMissing ? errorId : undefined}
        aria-invalid={isMissing}
        className={inputClasses}
      />
      {isMissing && (
        <p
          id={errorId}
          className="text-[11px] font-medium text-red-600 dark:text-red-400"
        >
          This field is required
        </p>
      )}
    </div>
  );
};

// ============================================================================
// Array Field Row
// ============================================================================

type ArrayItem = Record<string, string>;

function parseArrayValue(value: string): ArrayItem[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (item): item is ArrayItem =>
          item !== null && typeof item === 'object' && !Array.isArray(item),
      );
    }
  } catch {
    // Not valid JSON
  }
  return [];
}

const ArrayFieldRow = ({
  resolved,
  onValueChange,
  readOnly,
}: FieldRowProps) => {
  const { field, overrideKey, displayName, valueInfo, required, isMissing } = resolved;
  const schema = field.array_item_schema as ArrayItemProperty[];

  const items = useMemo(() => parseArrayValue(valueInfo.value), [valueInfo.value]);

  const commitItems = useCallback(
    (nextItems: ArrayItem[]) => {
      onValueChange(overrideKey, nextItems.length > 0 ? JSON.stringify(nextItems) : '');
    },
    [onValueChange, overrideKey],
  );

  const handleSubFieldChange = useCallback(
    (itemIndex: number, key: string, value: string) => {
      const next = items.map((item, i) =>
        i === itemIndex ? { ...item, [key]: value } : item,
      );
      commitItems(next);
    },
    [items, commitItems],
  );

  const handleAddItem = useCallback(() => {
    commitItems([...items, {}]);
  }, [items, commitItems]);

  const handleRemoveItem = useCallback(
    (index: number) => {
      commitItems(items.filter((_, i) => i !== index));
    },
    [items, commitItems],
  );

  const fieldId = field.id as string;

  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-zinc-200/80 bg-white/90 px-2.5 py-2 dark:border-zinc-700/80 dark:bg-zinc-950/60">
      <div
        className={cn(
          'flex items-center gap-x-1 text-[12px] font-medium',
          isMissing
            ? 'text-red-700 dark:text-red-400'
            : 'text-zinc-700 dark:text-zinc-300',
        )}
      >
        <span>{displayName}</span>
        {required && <span className="text-red-500">*</span>}
        <FieldSourceBadge source={valueInfo.source} isMissing={isMissing} inline />
      </div>

      {items.map((item, itemIndex) => (
        <div
          key={`${fieldId}-item-${itemIndex}`}
          className="flex items-start gap-1.5 rounded-md border border-zinc-200/90 bg-white p-1.5 dark:border-zinc-700/90 dark:bg-zinc-900"
        >
          <div className="flex-1 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${schema.length}, 1fr)` }}>
            {schema.map((prop) => (
              <div key={prop.key} className="flex flex-col gap-0.5">
                <label
                  htmlFor={`${fieldId}-${itemIndex}-${prop.key}`}
                  className="text-[10px] font-medium text-zinc-500 dark:text-zinc-400"
                >
                  {prop.label}
                  {prop.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <input
                  id={`${fieldId}-${itemIndex}-${prop.key}`}
                  type={prop.type === 'date' ? 'date' : 'text'}
                  value={item[prop.key] ?? ''}
                  onChange={(e) => handleSubFieldChange(itemIndex, prop.key, e.target.value)}
                  disabled={readOnly}
                  placeholder={prop.label}
                  className={cn(
                    'h-8 w-full rounded-md border px-2 text-[12px] transition-colors',
                    'focus:outline-none focus:ring-1 focus:ring-indigo-500',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'border-zinc-300 dark:border-zinc-600',
                  )}
                />
              </div>
            ))}
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => handleRemoveItem(itemIndex)}
              aria-label={`Remove item ${itemIndex + 1}`}
              className="mt-4 shrink-0 rounded p-1 text-zinc-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
      ))}

      {!readOnly && (
        <button
          type="button"
          onClick={handleAddItem}
          className="self-start rounded-md border border-dashed border-indigo-300 px-2.5 py-1 text-[11px] font-semibold text-indigo-600 transition-colors hover:bg-indigo-50 dark:border-indigo-500/40 dark:text-indigo-400 dark:hover:bg-indigo-500/10"
        >
          + Add item
        </button>
      )}

      {isMissing && (
        <p className="text-[11px] font-medium text-red-600 dark:text-red-400">
          This field is required
        </p>
      )}
    </div>
  );
};


// ============================================================================
// Helpers
// ============================================================================

function getPlaceholder(field: ReadTemplateField): string {
  switch (field.field_type) {
    case 'DATE':
      return 'YYYY-MM-DD';
    case 'IMAGE':
      return 'Enter image URL';
    case 'ARRAY':
      return 'Comma-separated values';
    default:
      return 'Enter value';
  }
}

// ============================================================================
// Skeleton
// ============================================================================

const FieldFillerSkeleton = ({ className }: { className?: string }) => (
  <div className={cn('space-y-2', className)}>
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="space-y-1">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-8 w-full rounded-md" />
      </div>
    ))}
  </div>
);
